import { AUDIENCE_CUTS, DEPTH_BANDS, FN_COLOR_OTHER } from "./constants";
import { selectionFor } from "./adapter";

/*
  Every panel's arithmetic: pure functions over the lead array, kept out of the components so they can be tested
  without rendering. Ported from the reference build's selectors.js and reshaped for the server payload.

  Two layers, and the distinction is what stops the page jumping about:
  - FIXED (from the payload, held): booked figures, row order, the pace target line, visibility. `buildIndexes`.
  - FILTERED (recomputed per selection): every count. `buildView`, one pass over the filtered leads.

  With no filters applied, buildView(model, model.leads) must equal the server `panels` numbers; the parity
  test asserts it.
*/

const numberFormat = new Intl.NumberFormat("en-GB");
export const fmt = (n) => numberFormat.format(n);

// 0/0 is NaN on screen, so every percentage goes through here.
export const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

// A job function's colour from the model's fixed lookup; a blank or unlisted value is grey.
export const fnColor = (model, name) => model.fnColors[name] || FN_COLOR_OTHER;

const inc = (map, key) => { if (key !== null && key !== undefined) { map.set(key, (map.get(key) || 0) + 1); } };

// Highest count first; ties broken by label so the order is stable and matches the server's.
const rank = (map, labelOf) => (
  Array.from(map.entries()).sort((a, b) => {
    if (b[1] !== a[1]) { return b[1] - a[1]; }
    const la = labelOf(a[0]).toLowerCase();
    const lb = labelOf(b[0]).toLowerCase();
    return la < lb ? -1 : (la > lb ? 1 : 0);
  })
);

/** Everything derived from the UNFILTERED leads. Computed once per model. */
export function buildIndexes(model) {
  const allByLineItem = new Map();
  const allByAsset = new Map();

  model.leads.forEach((lead) => {
    inc(allByLineItem, lead.lineItemId);
    inc(allByAsset, lead.assetKey);
  });

  let maxAsset = 1;
  allByAsset.forEach((n) => { if (n > maxAsset) { maxAsset = n; } });

  return { allByLineItem, allByAsset, maxAsset };
}

/** Everything the panels show, from one pass over `rows` (the filtered leads). */
export function buildView(model, rows, indexes) {
  const { lookups, pace } = model;

  const byLineItem = new Map();
  const byCompany = new Map();
  const byAsset = new Map();
  const assetByFn = new Map();
  const byCut = {};
  AUDIENCE_CUTS.forEach((cut) => { byCut[cut] = new Map(); });
  const perDay = [];

  // ---- the single pass ----
  for (let i = 0; i < rows.length; i++) {
    const lead = rows[i];

    inc(byLineItem, lead.lineItemId);
    inc(byCompany, lead.companyKey);
    inc(byAsset, lead.assetKey);
    inc(byCut.seniority, lead.seniority);
    inc(byCut.industry, lead.industry);
    inc(byCut.country, lead.country);

    if (lead.assetKey !== null && lead.jobFunction !== null) {
      let fnMap = assetByFn.get(lead.assetKey);
      if (!fnMap) { fnMap = new Map(); assetByFn.set(lead.assetKey, fnMap); }
      inc(fnMap, lead.jobFunction);
    }

    if (lead.dayIndex !== null) { perDay[lead.dayIndex] = (perDay[lead.dayIndex] || 0) + 1; }
  }

  const total = rows.length;
  const totalAll = model.leads.length;

  // ---- pace ----
  const target = [];
  const all = [];
  const selection = [];
  let run = 0;
  pace.daily.forEach((d) => {
    run += perDay[d.dayIndex] || 0;
    target.push([d.dayIndex, d.targetCumulative]);
    if (d.dayIndex <= pace.elapsedDays) {
      all.push([d.dayIndex, d.allCumulative]);
      selection.push([d.dayIndex, run]);
    }
  });
  // The target line always runs to the end of the campaign, even when `daily` stops at today.
  if (!target.length) { target.push([0, 0]); }
  if (target[target.length - 1][0] < pace.totalDays) { target.push([pace.totalDays, pace.booked]); }

  // ---- line items: booked is contracted and fixed; delivered/selected come from the leads ----
  const overallPercent = pct(totalAll, model.campaign.booked);
  const definitionById = {};
  model.lineItemDefinitions.forEach((d) => { definitionById[d.id] = d; });

  const lineItemRows = model.lineItemOrder.filter((id) => definitionById[id]).map((id) => {
    const definition = definitionById[id];
    const delivered = indexes.allByLineItem.get(id) || 0;
    const percent = pct(delivered, definition.booked);

    return {
      id,
      name: definition.name,
      booked: definition.booked,
      delivered,
      selected: byLineItem.get(id) || 0,
      percent,
      remaining: Math.max(0, definition.booked - delivered),
      behind: percent < overallPercent,
      selection: selectionFor(model, "lineItemId", id)
    };
  });

  // ---- companies: grouped on companyKey, never the display string ----
  const companyEntries = rank(byCompany, (key) => lookups.companyName[key] || "");
  const knownLeads = companyEntries.reduce((sum, e) => sum + e[1], 0);
  const depthLabels = model.limits.depthBandLabels.length === DEPTH_BANDS.length
    ? model.limits.depthBandLabels
    : DEPTH_BANDS.map((b) => b.label);
  const depthCounts = DEPTH_BANDS.map(() => 0);
  companyEntries.forEach((e) => {
    const band = DEPTH_BANDS.findIndex((b) => e[1] >= b.lo && e[1] <= b.hi);
    if (band >= 0) { depthCounts[band] += 1; }
  });
  const topLimit = model.limits.companiesTop || 8;
  const top = companyEntries.slice(0, topLimit).map(([key, count]) => ({
    key, label: lookups.companyName[key] || "", count, selection: selectionFor(model, "companyKey", key)
  }));

  const companies = {
    companyCount: companyEntries.length,
    knownCompanyLeadCount: knownLeads,
    unknownCompanyLeadCount: total - knownLeads,
    averageLeadsPerCompany: companyEntries.length ? Math.round((knownLeads / companyEntries.length) * 10) / 10 : 0,
    depth5Plus: depthCounts[depthCounts.length - 1],
    top,
    topMax: top.length ? top[0].count : 1,
    depthBands: depthLabels.map((label, i) => ({ label, count: depthCounts[i] })),
    depthBandMax: Math.max(...depthCounts, 1)
  };

  // ---- audience ----
  const audience = {};
  AUDIENCE_CUTS.forEach((cut) => {
    const limit = model.limits.audience[cut] || Infinity;
    const entries = rank(byCut[cut], (key) => key).slice(0, limit);

    audience[cut] = {
      rows: entries.map(([key, count]) => ({
        key, label: key, count, percentage: pct(count, total), selection: selectionFor(model, cut, key)
      })),
      max: entries.length ? entries[0][1] : 1
    };
  });

  // ---- assets: row order is fixed (assetOrder), segments follow the fixed job function order ----
  const namedFunctions = model.jobFunctionOrder.filter((fn) => fn.toLowerCase() !== "other");
  const assets = model.assetOrder.map((key) => {
    const count = byAsset.get(key) || 0;
    const fnMap = assetByFn.get(key) || new Map();

    const segments = model.jobFunctionOrder
      .filter((fn) => fnMap.get(fn))
      .map((fn) => ({
        jobFunctionKey: fn,
        label: fn,
        count: fnMap.get(fn),
        percentageOfAsset: pct(fnMap.get(fn), count),
        selection: selectionFor(model, "jobFunction", fn)
      }));

    // Leads with no (or an unlisted) job function fill the rest of the bar as one grey, non-clickable segment.
    const withFunction = segments.reduce((sum, segment) => sum + segment.count, 0);
    if (count > withFunction) {
      segments.push({ jobFunctionKey: "", label: "", count: count - withFunction, percentageOfAsset: pct(count - withFunction, count), selection: null });
    }

    // "Other" is a residual, never named as the strongest function.
    const strongest = namedFunctions
      .map((fn) => [fn, fnMap.get(fn) || 0])
      .sort((a, b) => b[1] - a[1])[0];

    return {
      key,
      label: lookups.assetName[key] || key,
      count,
      share: pct(count, total),
      segments,
      strongest: (count && strongest && strongest[1]) ? { label: strongest[0], percentage: pct(strongest[1], count) } : null,
      selection: selectionFor(model, "assetKey", key)
    };
  });

  return {
    total,
    totalAll,
    pace: { target, all, selection, delivered: total },
    lineItems: { rows: lineItemRows, overallPercent },
    companies,
    audience,
    assets: { rows: assets, max: indexes.maxAsset },
    register: rows
  };
}
